import { PropType, defineComponent } from "vue";
import { removeDuplicatesObject } from "@mv/flow-shared-utils/functions/object";
import { Template, TemplateCategory, TemplateCategoryListResponse } from "@/interfaces/sdk-templates";
import { EventBus } from "@/utils/event-bus";
import { ETypeNotification } from "@/interfaces/i-notification";
import { createTemplate, fetchTemplateCategories, updateTemplate } from "@/store/templates/actions";

export default defineComponent({
  name: "sdk-template-create-dialog",
  props: {
    visible: {
      type: Boolean,
      required: true,
      default: () => {
        return false;
      },
    },
    /* A prop abaixo só será passada caso o modal seja usado para editar um template */
    templateData: {
      type: Object as PropType<Template>,
      default: () => {
        return {} as Template;
      },
    },
  },
  data() {
    return {
      isLoading: false,
      templateName: "",
      templateDescription: "",
      categoryId: null as number | null,
      fileWasSelected: false,
      file: {} as Blob,
      categoriesInputWasNotUsed: true,
      categoriesPaginationConfig: {
        currentPage: 1,
        totalPage: 0,
        pageSize: 0,
        totalElements: 0,
        last: false,
      },
      isLoadingCategories: false,
      categoriesList: [] as TemplateCategory[],
      categoryQuery: "",
    };
  },
  computed: {
    saveButtonText(): string {
      return this.templateData ? this.$tc("global.buttons.update") : this.$tc("global.buttons.save");
    },
    dialogTitle(): string {
      return this.templateData ? this.$tc("templateViewer.editTemplate") : this.$tc("templateViewer.newTemplate");
    },
    blockTemplateCreation(): boolean {
      if (this.templateData) {
        return !this.templateName || !this.templateDescription || !this.categoryId;
      }
      return !this.templateName || !this.templateDescription || !this.categoryId || !this.fileWasSelected;
    },
  },
  methods: {
    validaFileSelection(event: any) {
      if (event.target.files && event.target.files.length) {
        this.fileWasSelected = true;
        this.file = event.target.files[0];
      } else {
        this.fileWasSelected = false;
        this.file = Object.assign({}, {} as Blob);
      }
    },
    cleanupData() {
      this.templateName = "";
      this.templateDescription = "";
      this.categoryId = null;
      this.categoriesInputWasNotUsed = true;
      this.categoriesPaginationConfig = {
        currentPage: 1,
        totalPage: 0,
        pageSize: 0,
        totalElements: 0,
        last: false,
      };
      this.categoriesList = [];
      this.categoryQuery = "";
      this.fileWasSelected = false;

      const fileInput = document.getElementById("template-file-input");
      if (fileInput) {
        (fileInput as HTMLInputElement).value = "";
      }
    },
    getCategories() {
      this.isLoadingCategories = true;
      this.$store
        .dispatch(fetchTemplateCategories(this.categoriesPaginationConfig.currentPage, this.categoryQuery))
        .then((categoryList: TemplateCategoryListResponse) => {
          this.categoriesPaginationConfig.totalPage = categoryList.pagination.totalPages;
          this.categoriesPaginationConfig.last = categoryList.pagination.last;
          this.categoriesList.push(...categoryList.data);

          this.categoriesList = removeDuplicatesObject(this.categoriesList, "id");
          this.categoriesInputWasNotUsed = false;
        })
        .catch((err) => {
          EventBus.$emit("emmit-notification", {
            type: ETypeNotification.DANGER,
            title: this.$t("templateViewer.categoryListLoadError"),
            message: err[0],
          });
        })
        .finally(() => {
          this.isLoadingCategories = false;
        });
    },
    getCategoriesHandler(loadMore = false) {
      if (this.categoriesInputWasNotUsed) {
        this.getCategories();
      } else if (!this.categoriesInputWasNotUsed && typeof loadMore === "boolean" && loadMore) {
        this.categoriesPaginationConfig.currentPage = this.categoriesPaginationConfig.currentPage + 1;
        this.getCategories();
      }
    },
    searchCategories(query: string) {
      if (query) {
        this.categoryQuery = query;
      } else {
        this.categoryQuery = "";
      }
      this.categoriesPaginationConfig.currentPage = 1;
      this.categoriesList = [];
      this.getCategories();
    },
    resetField() {
      this.categoryQuery = "";
      this.categoriesPaginationConfig.currentPage = 1;
      this.categoriesList = [];
      this.getCategories();
      this.categoriesInputWasNotUsed = true;
    },
    categoriesInputBlurHandler(show: boolean) {
      if (!show && this.categoryQuery) {
        setTimeout(() => {
          this.resetField();
        }, 500);
      }
    },
    closeModal() {
      this.$emit("closed");
    },
    finishCreation() {
      this.$emit("created");
      this.closeModal();
    },
    saveButtonClickHandler() {
      if (this.templateData) {
        this.updateProcessTemplate();
      } else {
        this.createProcessTemplate();
      }
    },
    createProcessTemplate() {
      this.isLoading = true;
      this.$store
        .dispatch(
          createTemplate({ name: this.templateName, categoryId: this.categoryId as number, description: this.templateDescription, file: this.file })
        )
        .then(() => {
          this.finishCreation();
        })
        .catch((err) => {
          EventBus.$emit("emmit-notification", {
            type: ETypeNotification.DANGER,
            title: this.$t("templateViewer.templateCreateError"),
            message: err[0],
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    updateProcessTemplate() {
      this.isLoading = true;
      this.$store
        .dispatch(updateTemplate(this.templateData.id, this.categoryId as number, this.templateDescription, this.templateName))
        .then(() => {
          EventBus.$emit("emmit-notification", {
            type: ETypeNotification.SUCCESS,
            title: this.$t("templateViewer.templateUpdateSucess"),
          });
          this.$emit("updated");
          this.closeModal();
        })
        .catch((err) => {
          EventBus.$emit("emmit-notification", {
            type: ETypeNotification.DANGER,
            title: this.$t("templateViewer.templateUpdateError"),
            message: err[0],
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    dialogOpenHandler() {
      if (this.templateData) {
        this.categoriesList = [{ name: this.templateData.categoryName, id: this.templateData.categoryId }];
        this.templateName = this.templateData.name;
        this.templateDescription = this.templateData.description;
        this.categoryId = this.templateData.categoryId;
      }
    },
  },
});
